import React, { useCallback, useEffect, useState } from 'react';
import {
    DictCategory,
    DictEntry,
    ProcessArch,
    createDict,
    createDictCategory,
    deleteDictCategory,
    fetchDictCategoryDeletePreview,
    updateDictCategory,
    listDictCategoryGroups,
    DictCategoryGroup,
    createProcessArch,
    deleteDict,
    deleteProcessArch,
    listDict,
    listDictCategories,
    listProcessArchsAdmin,
    updateDict,
    updateProcessArch,
} from '../../api/dict';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import DeleteCascadeModal from '../../components/DeleteCascadeModal';
import SearchSelect from '../../components/SearchSelect';
import { DeleteCascadePayload } from '../../api/cascade';
import { listMMGroups } from '../../api/mmode';
import { Link, useHistory, useLocation } from 'react-router-dom';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { alertDialog } from '../../utils/dialog';
import { useCan, useMe } from '../../store';

// 通用字典 / 工艺架构 统一维护页.
// 设计:
// - tab 一级: 加工类型 / 工艺架构
// - 加工类型 tab 二级 chip: 按工艺架构 (laser-frame / knife / ...) 分别维护, 每个 arch 一份独立列表
// - 工艺架构 tab 无二级
// 权限: 两个 tab 都登录即可操作 (写操作统一走审批工单).

type Source = string; // 类目 code (dict_category 数据驱动) + 'process_arch' (独立表)

// 类目分组: 存在 dict_category.grp 上 (2026-08-12), 词表由后端 /dict-category-groups 给,
// 新建类目时必选、之后可在类目卡片右上角改 —— 前端不再维护 code→分组 的硬编码映射表.
// 工艺架构 / 加工模式两个 tab 不是 dict_category (独立表), 固定归"平台结构".
const NON_DICT_GRP: Record<string, string> = {
    process_arch: 'platform',
    mm_group: 'platform',
};

// EntryRow 是 UI 统一行结构.
interface EntryRow {
    code: string;
    label: string;
    icon?: string;
    sort: number;
    isEnabled: boolean;
    /** 仅工艺架构 tab 用: 材料键粒度 (param_table 由后端按 code 派生, 前端不录入). 其它字典忽略. */
    materialKeyKind?: string;
}

interface Adapter {
    label: string;
    description: string;
    /** 是否支持 scope 维度 (加工类型按 arch 分组) */
    hasScope: boolean;
    /** 只读 tab (数据源在别处维护, 提供跳转); 隐藏 新增/编辑/删除 */
    readOnly?: { hint: string; to: string };
    list: (scope: string) => Promise<EntryRow[]>;
    /** 写操作返回后端 message (普通用户走审批时含"已提交审批"); 无 message 语义的返回 ''. */
    create: (scope: string, e: EntryRow) => Promise<string>;
    update: (
        scope: string,
        origCode: string,
        e: Partial<EntryRow>,
    ) => Promise<string>;
    remove: (scope: string, code: string) => Promise<string>;
}

const dictAdapter = (
    category: string,
    label = '加工类型',
    description = '按工艺架构维护各自的加工类型列表; 选了某 arch 后, 参数管理"加工类型"下拉只显示这里启用的项.',
    hasScope = true,
): Adapter => ({
    label,
    description,
    hasScope,
    list: async (scope) => {
        const list = await listDict(category, { scope, includeDisabled: true });
        return list.map((d: DictEntry) => ({
            code: d.code,
            label: d.label,
            icon: d.icon,
            sort: d.sort,
            isEnabled: d.isEnabled,
        }));
    },
    create: async (scope, e) => {
        const res = await createDict(category, { ...e, scopeKey: scope });
        return res.message;
    },
    update: async (scope, code, e) => {
        const res = await updateDict(category, code, e, { scope });
        return res.message;
    },
    remove: async (scope, code) => {
        const res = await deleteDict(category, code, { scope });
        return res.message;
    },
});

// 加工模式 (mm_group) 只读视图: 真正的维护 (组+模式值+设备引用) 在「加工模式字典」页;
// 放进字典维护是为了与"矩阵字段组行来源枚举可选 加工模式"的上下文对齐.
const mmGroupAdapter: Adapter = {
    label: '加工模式',
    description:
        '加工模式(复用组), 参数真实键; 可作为矩阵字段组的行来源枚举。组/模式值/设备引用在「加工模式字典」维护, 此处只读。',
    hasScope: false,
    readOnly: { hint: '去 加工模式字典 维护', to: '/admin/data-dict?d=mmode' },
    list: async () => {
        const gs = await listMMGroups(true);
        return gs.map((g, i) => ({
            code: g.code,
            label: g.nameZh || g.code,
            sort: i + 1,
            isEnabled: true,
        }));
    },
    create: async () => '',
    update: async () => '',
    remove: async () => '',
};

const archAdapter: Adapter = {
    label: '工艺架构',
    description:
        '工艺架构枚举 (激光框机 / 振镜 / 刀切 / ...), 影响参数管理顶部架构 chip 列表 + 设备分组.',
    hasScope: false,
    list: async () => {
        const list = await listProcessArchsAdmin();
        return list.map((d: ProcessArch) => ({
            code: d.code,
            label: d.label,
            icon: d.icon,
            sort: d.sort,
            isEnabled: d.isEnabled,
            materialKeyKind: d.materialKeyKind || 'xcode',
        }));
    },
    create: async (_scope, e) => {
        const res = await createProcessArch({
            code: e.code,
            label: e.label,
            icon: e.icon,
            sort: e.sort,
            isEnabled: e.isEnabled,
            materialKeyKind: e.materialKeyKind,
        });
        return res.message;
    },
    update: async (_scope, code, e) => {
        const res = await updateProcessArch(code, {
            label: e.label,
            icon: e.icon,
            sort: e.sort,
            isEnabled: e.isEnabled,
            materialKeyKind: e.materialKeyKind,
        });
        return res.message;
    },
    remove: async (_scope, code) => {
        const res = await deleteProcessArch(code);
        return res.message;
    },
};

// 工艺框架的材料键粒度 (process_arch.material_key_kind)
const KEY_KIND_OPTS = [
    { value: 'xcode', label: 'xcode (单材料键, 如激光/刀切)' },
    { value: 'dual_xcode', label: 'dual_xcode (膜+基材, 如热压)' },
    { value: 'category_code', label: 'category_code (二级分类, 如喷墨)' },
];

function DictAdmin() {
    // 字典条目的写权按类目所属分组的归属资源判 (2026-09-13, 与后端 dict.requireDictWrite 同口径):
    // 材料属性→材料管理员 / 设备能力→设备管理员 / 参数字段枚举→工艺参数管理员;
    // 平台结构 (加工类型 / 工艺架构) 与未归类的只给系统管理员.
    const isAdmin = useMe().isAdmin;
    const matC = useCan('material', 'create');
    const matU = useCan('material', 'update');
    const matD = useCan('material', 'delete');
    const devC = useCan('device', 'create');
    const devU = useCan('device', 'update');
    const devD = useCan('device', 'delete');
    const parC = useCan('param_row', 'create');
    const parU = useCan('param_row', 'update');
    const parD = useCan('param_row', 'delete');
    // 分组 → 写权 (布尔量做 memo 依赖: 对象字面量每次渲染都是新引用, 会把下游 useMemo/useCallback
    // 全部打脏 → visibleTabs→active→load 每轮重建, 列表永远停在"加载中").
    const permOfGroup = React.useCallback(
        (grp?: string) => {
            if (grp === 'material')
                return { create: matC, update: matU, delete: matD };
            if (grp === 'device')
                return { create: devC, update: devU, delete: devD };
            if (grp === 'param')
                return { create: parC, update: parU, delete: parD };
            return { create: isAdmin, update: isAdmin, delete: isAdmin }; // platform / other
        },
        [matC, matU, matD, devC, devU, devD, parC, parU, parD, isAdmin],
    );
    const canMaintainGroup = React.useCallback(
        (grp: string) => {
            const p = permOfGroup(grp);
            return p.create || p.update || p.delete;
        },
        [permOfGroup],
    );
    // 平台结构 (加工类型 / 工艺架构 / 加工模式) 是三个域共用的结构键: 材料 / 设备 / 工艺参数管理员
    // 都看得到 (只读), 维护仍只归系统管理员 (2026-09-13).
    // 「去 加工模式字典 维护」这类跳转: 没有目标资源的维护权就不给入口 (点过去也是无权访问)
    const canMaintainMMode = useCan('mm_mode', 'update');
    const canSeeGroup = React.useCallback(
        (grp: string) =>
            grp === 'platform'
                ? canMaintainGroup('material') ||
                  canMaintainGroup('device') ||
                  canMaintainGroup('param') ||
                  canMaintainGroup('platform')
                : canMaintainGroup(grp),
        [canMaintainGroup],
    );
    // 只读访问者只看在用的字典值 (停用项是维护过程态)
    // 支持 ?tab=process_arch 直达 (如 Schema 管理「新增设备模板」跳转到工艺架构维护).
    // 用 react-router 的 location.search 而非 window.location.search:
    // HashRouter 下 query 在 # 之后 (hash 里), window.location.search 恒为空, 会误落默认 tab.
    const location = useLocation();
    const history = useHistory();
    const [activeId, setActiveId] = useState<Source>(() => {
        const t = new URLSearchParams(location.search).get('tab');
        return (t as Source) || 'process_type';
    });
    // 切 tab 同步回 URL: 否则刷新/后退会退回深链带进来的那个 tab, 与页面上看到的不一致.
    // 只改 tab 一个键 — 本页嵌在「数据字典」页的 tab 里 (?d=dict), 整串重写会把 d 冲掉.
    const switchTab = (id: Source) => {
        setActiveId(id);
        const next = new URLSearchParams(location.search);
        next.set('tab', id);
        history.replace(`${location.pathname}?${next.toString()}`);
    };
    // 类目数据驱动 (dict_category): 字典维护可自助新增类目 (2026-08-09)
    const [cats, setCats] = useState<DictCategory[]>([]);
    const loadCats = useCallback(() => {
        listDictCategories(true)
            .then(setCats)
            .catch(() => setCats([]));
    }, []);
    useEffect(() => {
        loadCats();
    }, [loadCats]);
    // tabs = 类目表 (process_type 在首) + 工艺架构 (独立表, 插在 process_type 后)
    const visibleTabs = React.useMemo(() => {
        const dictTabs = cats.map((c) => ({
            id: c.code as Source,
            adapter: dictAdapter(
                c.code,
                c.label,
                c.description || '',
                c.scoped,
            ),
        }));
        const i = dictTabs.findIndex((t) => t.id === 'process_type');
        dictTabs.splice(
            i + 1,
            0,
            { id: 'process_arch', adapter: archAdapter },
            { id: 'mm_group', adapter: mmGroupAdapter },
        );
        // 只留自己能维护的分组 (2026-09-13): 材料管理员只看材料属性, 设备管理员只看设备能力,
        // 参数管理员只看参数字段枚举; 平台结构 (加工类型 / 工艺架构 / 加工模式) 只给系统管理员.
        return dictTabs.filter((t) =>
            canSeeGroup(
                NON_DICT_GRP[t.id] ||
                    cats.find((c) => c.code === t.id)?.grp ||
                    'other',
            ),
        );
    }, [cats, canSeeGroup]);
    // activeId 可能指向被权限过滤掉的类目 (URL 直达 / 默认值): 统一回落到第一个可见 tab,
    // 后面的写权判定与 targetCode 都用这个 effectiveId, 免得按一个看不见的类目判权 (2026-09-13).
    const activeTab =
        visibleTabs.find((t) => t.id === activeId) || visibleTabs[0];
    const effectiveId = activeTab?.id || activeId;
    const active = activeTab?.adapter || archAdapter;
    // 分组词表 (后端 /dict-category-groups)
    const [catGroups, setCatGroups] = useState<DictCategoryGroup[]>([]);
    useEffect(() => {
        listDictCategoryGroups()
            .then(setCatGroups)
            .catch(() => setCatGroups([]));
    }, []);
    // tab 按类目自己的 grp 归堆 (组内保持类目表顺序); grp 认不出来的落最后一组
    const groupedTabs = React.useMemo(() => {
        if (catGroups.length === 0) return [{ title: '', items: visibleTabs }];
        const grpOf = (id: string) =>
            NON_DICT_GRP[id] || cats.find((c) => c.code === id)?.grp || 'other';
        const known = new Set(catGroups.map((g) => g.code));
        return (
            catGroups
                .map((g) => ({
                    code: g.code,
                    title: g.label,
                    items: visibleTabs.filter((t) => {
                        const grp = grpOf(t.id);
                        return (
                            grp === g.code ||
                            (!known.has(grp) && g.code === 'other')
                        );
                    }),
                }))
                // 只留自己能维护的分组 (2026-09-13): 材料管理员只看材料属性, 设备管理员只看设备能力,
                // 参数管理员只看参数字段枚举; 平台结构与未归类只给系统管理员.
                .filter((r) => r.items.length > 0 && canSeeGroup(r.code))
                .map(({ title, items }) => ({ title, items }))
        );
    }, [visibleTabs, cats, catGroups, canSeeGroup]);

    const curCat = cats.find((c) => c.code === effectiveId);

    // 类目弹窗: 新建 / 编辑共用一套表单 (编辑只改 label/描述/分组, code 是业务键不可改)
    const [catMode, setCatMode] = useState<'create' | 'edit' | null>(null);
    const [catCode, setCatCode] = useState('');
    const [catLabel, setCatLabel] = useState('');
    const [catDesc, setCatDesc] = useState('');
    const [catGrp, setCatGrp] = useState('param');
    const [catBusy, setCatBusy] = useState(false);
    const openCreateCategory = () => {
        setCatCode('');
        setCatLabel('');
        setCatDesc('');
        setCatGrp('param');
        setCatMode('create');
    };
    const openEditCategory = () => {
        if (!curCat) return;
        setCatCode(curCat.code);
        setCatLabel(curCat.label);
        setCatDesc(curCat.description || '');
        setCatGrp(curCat.grp || 'other');
        setCatMode('edit');
    };
    // 删除类目 (编辑弹窗内, 2026-09-08): 走通用删除预检弹窗 —— 列出条目明细 (随类目一并删) 与阻塞引用 (字段枚举来源 /
    // 矩阵行来源, 存在则不能删), 代替原生 confirm/alert; 报错在弹窗内展示.
    const [catDeleting, setCatDeleting] = useState(false);
    const onConfirmDeleteCategory = async (payload: DeleteCascadePayload) => {
        if (!curCat) return;
        const res = await deleteDictCategory(curCat.code, payload);
        setCatMode(null);
        setActiveId('process_type');
        loadCats();
        alertApprovalIfPending(res.message);
    };
    const onSubmitCategory = async () => {
        if (!catLabel.trim() || (catMode === 'create' && !catCode.trim())) {
            alertDialog('类目编码 / 名称 不能为空');
            return;
        }
        setCatBusy(true);
        try {
            const res =
                catMode === 'edit'
                    ? await updateDictCategory(catCode, {
                          label: catLabel.trim(),
                          description: catDesc.trim(),
                          grp: catGrp,
                      })
                    : await createDictCategory({
                          code: catCode.trim(),
                          label: catLabel.trim(),
                          description: catDesc.trim(),
                          grp: catGrp,
                          sort: cats.length + 1,
                      });
            setCatMode(null);
            loadCats();
            alertApprovalIfPending(res.message);
        } catch (e) {
            const act = catMode === 'edit' ? '保存类目' : '新建类目';
            alertDialog(
                `${act}失败: ${e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)}`,
            );
        } finally {
            setCatBusy(false);
        }
    };

    // 加工类型 tab 的二级 chip: 当前 scope (arch_code).
    // 默认 laser-frame; 切 tab 时如果 hasScope=false 用空串.
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    // 默认选中激光框机 (2026-08-11 起 laser 拆分为 laser-frame/laser-galvo);
    // 若 code 不在注册表, 下方 effect 会兜底落到第一个框架
    const [scope, setScope] = useState<string>('laser-frame');
    const effectiveScope = active.hasScope ? scope : '';

    const [list, setList] = useState<EntryRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // 新建/编辑 表单
    const [editing, setEditing] = useState<EntryRow | null>(null);
    const [formCode, setFormCode] = useState('');
    const [formLabel, setFormLabel] = useState('');
    const [formIcon, setFormIcon] = useState('');
    const [formSort, setFormSort] = useState(0);
    const [formEnabled, setFormEnabled] = useState(true);
    // 仅工艺架构 tab: 材料键粒度 (param_table 由后端按 code 派生, 无需前端录入)
    const [formKeyKind, setFormKeyKind] = useState('xcode');
    const [formBusy, setFormBusy] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const isArchTab = effectiveId === 'process_arch';
    // 工艺架构是平台结构 (只有系统管理员); 其余按当前类目的分组归属
    const perm = permOfGroup(isArchTab ? 'platform' : curCat?.grp);
    const canCreate = perm.create;
    const canUpdate = perm.update;
    const canDelete = perm.delete;
    const canManage = canUpdate || canDelete;
    const maintainer = canCreate || canUpdate || canDelete;

    // 批量操作 (2026-09-10): 主表按当前 tab 归属到不同资源域 —— 工艺框架是 process_arch,
    // 其余字典条目是 dict (targetCode 为 category/code[/scope] 复合键, 与单条删除同一套构造).
    // 加工模式 (mm_group) tab 是只读视图, 维护在「加工模式字典」页, 不给批量入口.
    const batchResource = isArchTab ? 'process_arch' : 'dict';
    const dictTargetCode = React.useCallback(
        (code: string) => {
            if (isArchTab) return code;
            return active.hasScope && scope
                ? `${effectiveId}/${code}/${scope}`
                : `${effectiveId}/${code}`;
        },
        [isArchTab, active.hasScope, effectiveId, scope],
    );
    const sel = useBatchSelect(
        React.useMemo(
            () => list.map((r) => dictTargetCode(r.code)),
            [list, dictTargetCode],
        ),
        [effectiveId, scope].join('|'),
    );

    // arch 列表 (给加工类型等 scoped tab 做二级 chip). 在「工艺架构」tab 改过启用状态后
    // 要重拉一次 —— 否则刚启用的框架得刷新整页才出现在 chip 里。
    const reloadArchs = useCallback(() => {
        listProcessArchsAdmin()
            .then((list) => {
                setArchs(list);
                // 兜底: 当前 scope 不在注册表 (如旧 code)、或落在未启用框架上时, 退到第一个启用的框架
                setScope((cur) => {
                    const hit = list.find((a) => a.code === cur);
                    if (hit?.isEnabled) return cur;
                    return (
                        list.find((a) => a.isEnabled)?.code ||
                        list[0]?.code ||
                        cur
                    );
                });
            })
            .catch(() => setArchs([]));
    }, []);
    useEffect(reloadArchs, [reloadArchs]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const rows = await active.list(effectiveScope);
            setList(maintainer ? rows : rows.filter((r) => r.isEnabled));
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [active, effectiveScope, maintainer]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        setFormCode('');
        setFormLabel('');
        setFormIcon('');
        setFormSort(list.length + 1);
        setFormEnabled(true);
        setFormKeyKind('xcode');
        setShowForm(true);
    };
    const openEdit = (r: EntryRow) => {
        setEditing(r);
        setFormCode(r.code);
        setFormLabel(r.label);
        setFormIcon(r.icon || '');
        setFormSort(r.sort);
        setFormEnabled(r.isEnabled);
        setFormKeyKind(r.materialKeyKind || 'xcode');
        setShowForm(true);
    };

    const onSubmit = async () => {
        if (!formCode.trim() || !formLabel.trim()) {
            alertDialog('code / label 不能为空');
            return;
        }
        setFormBusy(true);
        try {
            let message: string;
            // 工艺架构 tab 额外带上材料键粒度; 其它字典不传 (adapter 会忽略).
            const archExtra = isArchTab ? { materialKeyKind: formKeyKind } : {};
            if (editing) {
                message = await active.update(effectiveScope, editing.code, {
                    label: formLabel.trim(),
                    icon: formIcon.trim(),
                    sort: formSort,
                    isEnabled: formEnabled,
                    ...archExtra,
                });
            } else {
                message = await active.create(effectiveScope, {
                    code: formCode.trim(),
                    label: formLabel.trim(),
                    icon: formIcon.trim() || undefined,
                    sort: formSort,
                    isEnabled: formEnabled,
                    ...archExtra,
                });
            }
            setShowForm(false);
            // 走审批流时数据未变, load 只是回读原列表
            await load();
            if (isArchTab) reloadArchs(); // 新建/改名/启停框架后, chip 列表跟着变
            alertApprovalIfPending(message);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog((editing ? '更新失败: ' : '新建失败: ') + msg);
        } finally {
            setFormBusy(false);
        }
    };

    const onToggleEnabled = async (r: EntryRow) => {
        try {
            const message = await active.update(effectiveScope, r.code, {
                isEnabled: !r.isEnabled,
            });
            await load();
            if (isArchTab) reloadArchs(); // 启用了就让它立刻出现在 chip 里, 停用了就消失
            alertApprovalIfPending(message);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog('切换状态失败: ' + msg);
        }
    };

    // 删条目: 同一套删除确认弹窗 (无关联数据, 不拉预检); 失败信息在弹窗内展示
    const [deleting, setDeleting] = useState<EntryRow | null>(null);
    const onConfirmDeleteEntry = async () => {
        if (!deleting) return;
        const message = await active.remove(effectiveScope, deleting.code);
        await load();
        if (isArchTab) reloadArchs();
        alertApprovalIfPending(message);
    };

    const scopeLabel = archs.find((a) => a.code === scope)?.label || scope;
    // 未启用的工艺架构 (占位框架, 如刺绣 / FDM / laser-sla) 一律不列: 它们没有设备也没有参数表,
    // 列出来只会让人以为可以去维护。要维护它们的字典, 先去「工艺架构」tab 把框架启用。
    const visibleArchs = archs.filter((a) => a.isEnabled);

    return (
        <div className="space-y-4">
            <div>
                <div className="text-base font-semibold">字典维护</div>
                <div className="text-xs text-slate-500">
                    维护参数管理表单里的下拉选项; 管理员操作立即生效,
                    普通用户提交后走审批工单.
                </div>
            </div>

            {/* tab 一级 (类目数据驱动, 按用途分组) + 新建类目 */}
            <div className="space-y-1.5">
                {groupedTabs.map((row) => (
                    <div
                        key={row.title}
                        className="flex flex-wrap gap-2 items-center"
                    >
                        <span className="text-xs text-slate-400 w-[68px] shrink-0 text-right pr-1">
                            {row.title}
                        </span>
                        {row.items.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className={`px-3 py-1.5 text-sm rounded ${
                                    t.id === effectiveId
                                        ? 'bg-brand-500 text-white'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                                onClick={() => switchTab(t.id)}
                            >
                                {t.adapter.label}
                            </button>
                        ))}
                    </div>
                ))}
                <div className="flex flex-wrap gap-2 items-center">
                    <span className="w-[68px] shrink-0" />
                    <button
                        type="button"
                        className="px-3 py-1.5 text-sm rounded border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50"
                        title="新建字典类目 (如 压力档位); 建好后可作为矩阵字段组的行来源枚举 / 字段枚举来源"
                        onClick={openCreateCategory}
                        hidden={!canCreate}
                    >
                        + 新建类目
                    </button>
                </div>
            </div>

            <DeleteCascadeModal
                open={catDeleting}
                title="删除字典类目"
                targetLabel={curCat ? `${curCat.label} (${curCat.code})` : ''}
                loadPreview={() =>
                    fetchDictCategoryDeletePreview(curCat?.code || '')
                }
                onConfirm={onConfirmDeleteCategory}
                onClose={() => setCatDeleting(false)}
            />

            <DeleteCascadeModal
                open={!!deleting}
                title="删除字典条目"
                targetLabel={
                    deleting ? `${deleting.label} (${deleting.code})` : ''
                }
                warning="后续参数管理表单的对应下拉将不再展示此项; 已用该值保存的记录不会被改动"
                onConfirm={onConfirmDeleteEntry}
                onClose={() => setDeleting(null)}
            />

            {/* 类目弹窗: 新建 / 编辑 */}
            {catMode && (
                <div
                    className="fixed inset-0 !m-0 z-40 grid place-items-center"
                    style={{ background: 'rgba(15,23,42,.45)' }}
                >
                    <div className="bg-white rounded-xl shadow-xl p-5 w-[460px] space-y-3">
                        <div className="text-sm font-semibold">
                            {catMode === 'edit'
                                ? '编辑字典类目'
                                : '新建字典类目'}
                        </div>
                        <div className="text-xs text-slate-500">
                            类目 = 一组受控枚举 (如"压力档位");
                            建好后在本页维护条目,
                            并可作为矩阵字段组的行来源枚举。
                        </div>
                        <div>
                            <label className="field-label">
                                类目编码 (英文, 建后不可改)
                            </label>
                            <input
                                className="inp w-full font-mono"
                                value={catCode}
                                placeholder="pressure_level"
                                disabled={catMode === 'edit'}
                                onChange={(e) => setCatCode(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="field-label">类目名称</label>
                            <input
                                className="inp w-full"
                                value={catLabel}
                                placeholder="压力档位"
                                onChange={(e) => setCatLabel(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="field-label">
                                所属分组 (决定摆在哪一行, 之后可改)
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={catGrp}
                                onChange={(v) => setCatGrp(v)}
                                options={catGroups.map((g) => ({
                                    value: g.code,
                                    label: g.label,
                                }))}
                            />
                            <div className="text-[11px] text-slate-400 mt-1">
                                {catGroups.find((g) => g.code === catGrp)
                                    ?.hint || ''}
                            </div>
                        </div>
                        <div>
                            <label className="field-label">
                                用途说明 (可选)
                            </label>
                            <input
                                className="inp w-full"
                                value={catDesc}
                                onChange={(e) => setCatDesc(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            {catMode === 'edit' && (
                                <button
                                    type="button"
                                    className="btn-ghost text-rose-600 border-rose-200 hover:bg-rose-50"
                                    disabled={catBusy}
                                    title="删除该字典类目本身 (先清空条目; 被字段枚举来源/矩阵行来源或平台代码引用时不允许删)"
                                    onClick={() => setCatDeleting(true)}
                                    hidden={!canDelete}
                                >
                                    删除类目
                                </button>
                            )}
                            <div className="flex-1" />
                            <button
                                type="button"
                                className="btn-ghost"
                                disabled={catBusy}
                                onClick={() => setCatMode(null)}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                disabled={catBusy}
                                onClick={onSubmitCategory}
                            >
                                {catBusy
                                    ? '提交中…'
                                    : catMode === 'edit'
                                      ? '保存'
                                      : '创建'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* tab 二级: 仅加工类型显示 arch chip */}
            {active.hasScope && (
                <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-xs text-slate-400 mr-1">
                        工艺架构:
                    </span>
                    {visibleArchs.map((a) => (
                        <span
                            key={a.code}
                            className={`tag cursor-pointer ${
                                a.code === scope
                                    ? 'bg-brand-500 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            onClick={() => setScope(a.code)}
                        >
                            {a.icon} {a.label}
                        </span>
                    ))}
                </div>
            )}

            <div className="card p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-slate-500">
                        {active.description}
                        {active.hasScope && (
                            <span className="ml-2 text-brand-600">
                                当前: {scopeLabel}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {/* 改名/改说明/改分组/删除 都在弹窗里 (code 不可改; 内置类目同样可改名) */}
                        {curCat && (
                            <button
                                type="button"
                                className="btn-ghost text-xs"
                                title="修改类目名称 / 用途说明 / 所属分组 (编码 code 是业务键, 不可改)"
                                onClick={openEditCategory}
                                hidden={!canUpdate}
                            >
                                编辑类目
                            </button>
                        )}
                        {!active.readOnly && (
                            <BatchActions
                                resource={batchResource}
                                entityLabel={
                                    isArchTab
                                        ? '工艺框架'
                                        : active.label || '字典条目'
                                }
                                deleteKeyField="code"
                                buildDeleteData={(target) => {
                                    if (isArchTab) return { code: target };
                                    const [category, code, sc] =
                                        target.split('/');
                                    return { category, code, scope: sc || '' };
                                }}
                                selected={sel.selectedKeys}
                                onClear={sel.clear}
                                onDone={load}
                            />
                        )}
                        {active.readOnly && canMaintainMMode ? (
                            <Link
                                to={active.readOnly.to}
                                className="btn-ghost text-xs"
                            >
                                ↗ {active.readOnly.hint}
                            </Link>
                        ) : (
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={openCreate}
                                hidden={!canCreate}
                            >
                                + 新增
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50 mb-3">
                        {error}
                    </div>
                )}

                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                {!active.readOnly && (
                                    <HeaderCheckbox
                                        hidden={!canDelete}
                                        sel={sel}
                                    />
                                )}
                            </th>
                            <th className="th w-16">排序</th>
                            <th className="th w-12"></th>
                            <th className="th">code</th>
                            <th className="th">label</th>
                            <th className="th w-24" hidden={!maintainer}>
                                状态
                            </th>
                            <th className="th w-40" hidden={!canManage}>
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={7}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={7}
                                >
                                    {active.hasScope
                                        ? `${scopeLabel} 还没配置加工类型, 点 "+ 新增" 添加`
                                        : '暂无数据, 点 "+ 新增" 添加'}
                                </td>
                            </tr>
                        )}
                        {list.map((r) => {
                            // 禁用行只淡化信息列 (排序/icon/code/label), 状态与操作列保持全亮:
                            // 禁用的框架恰恰是最需要点"编辑/启用/删除"的对象, 不能连按钮一起灰掉.
                            const dim = r.isEnabled ? '' : 'opacity-50';
                            return (
                                <tr key={r.code} className="hover:bg-slate-50">
                                    <td className="td" hidden={!canDelete}>
                                        {!active.readOnly && (
                                            <RowCheckbox
                                                hidden={!canDelete}
                                                sel={sel}
                                                k={dictTargetCode(r.code)}
                                            />
                                        )}
                                    </td>
                                    <td
                                        className={`td font-mono text-xs ${dim}`}
                                    >
                                        {r.sort}
                                    </td>
                                    <td className={`td text-lg ${dim}`}>
                                        {r.icon || ''}
                                    </td>
                                    <td
                                        className={`td font-mono text-xs ${dim}`}
                                    >
                                        {r.code}
                                    </td>
                                    <td className={`td ${dim}`}>{r.label}</td>
                                    <td className="td" hidden={!maintainer}>
                                        {active.readOnly && canMaintainMMode ? (
                                            <span className="text-xs text-slate-400">
                                                —
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onToggleEnabled(r)
                                                }
                                                className={`text-xs px-2 py-0.5 rounded ${
                                                    r.isEnabled
                                                        ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                }`}
                                            >
                                                {r.isEnabled
                                                    ? '✓ 启用'
                                                    : '✗ 禁用'}
                                            </button>
                                        )}
                                    </td>
                                    <td
                                        className="td text-xs space-x-3 whitespace-nowrap"
                                        hidden={!canManage}
                                    >
                                        {active.readOnly && canMaintainMMode ? (
                                            <Link
                                                to={active.readOnly.to}
                                                className="text-brand-600 hover:underline"
                                            >
                                                去维护 ↗
                                            </Link>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="text-brand-600 hover:underline"
                                                    onClick={() => openEdit(r)}
                                                    hidden={!canUpdate}
                                                >
                                                    编辑
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-danger-text"
                                                    onClick={() =>
                                                        setDeleting(r)
                                                    }
                                                    hidden={!canDelete}
                                                >
                                                    删除
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showForm && (
                <div
                    className="fixed inset-0 !m-0 bg-black/30 flex items-center justify-center z-50"
                    onClick={() => setShowForm(false)}
                >
                    <div
                        className="card p-5 w-[480px] space-y-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-base font-semibold">
                            {editing
                                ? `编辑 · ${editing.label}`
                                : `新增 ${active.label}${active.hasScope ? ` (${scopeLabel})` : ''}`}
                        </div>
                        <div>
                            <label className="field-label">
                                <span className="text-rose-500">* </span>code
                            </label>
                            <input
                                className="inp font-mono"
                                value={formCode}
                                onChange={(e) => setFormCode(e.target.value)}
                                disabled={!!editing}
                                placeholder="如 engrave / cut"
                            />
                            {editing && (
                                <div className="text-[11px] text-slate-400 mt-1">
                                    code 是业务主键, 不可改; 要换 code
                                    请删除后重新建.
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="field-label">
                                <span className="text-rose-500">* </span>label
                                (展示名)
                            </label>
                            <input
                                className="inp"
                                value={formLabel}
                                onChange={(e) => setFormLabel(e.target.value)}
                                placeholder="如 雕刻 / 切割"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="field-label">
                                    icon (emoji, 可选)
                                </label>
                                <input
                                    className="inp"
                                    value={formIcon}
                                    onChange={(e) =>
                                        setFormIcon(e.target.value)
                                    }
                                    placeholder="🔲 / 🔪"
                                />
                            </div>
                            <div>
                                <label className="field-label">
                                    排序 (越小越靠前)
                                </label>
                                <input
                                    className="inp"
                                    type="number"
                                    value={formSort}
                                    onChange={(e) =>
                                        setFormSort(Number(e.target.value || 0))
                                    }
                                />
                            </div>
                        </div>
                        <div>
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={formEnabled}
                                    onChange={(e) =>
                                        setFormEnabled(e.target.checked)
                                    }
                                />
                                启用 (禁用项不在参数管理表单里展示)
                            </label>
                        </div>
                        {isArchTab && (
                            <div className="pt-2 border-t border-slate-100">
                                <label className="field-label">
                                    材料键粒度 (material_key_kind)
                                </label>
                                <SearchSelect
                                    className="w-full"
                                    value={formKeyKind}
                                    onChange={(v) => setFormKeyKind(v)}
                                    options={KEY_KIND_OPTS}
                                />
                                <div className="text-[11px] text-slate-400 mt-1">
                                    参数表名 (param_table) 由后端按 code
                                    自动派生 (param_&lt;code&gt;), 无需填写。
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => setShowForm(false)}
                                disabled={formBusy}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={onSubmit}
                                disabled={formBusy}
                            >
                                {formBusy ? '保存中…' : '保存'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DictAdmin;
